import { useMemo, useState } from 'react';

import {
  Button,
  SearchForm,
  TableContainer,
  TableHeader,
  Typography,
} from '@occc/ui-design-system';
import { ContainerColumnLeft } from '@occc-app/design-system';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { useGetSiteManagementLocations } from '@occc-app/dataAccessLayer/location/queries';
import { pluralize } from '@occc-app/lib/strings';

import LocationsTable from './locations-table.component';

import { RetailerNetworkUpdateHandler } from '../../../retailer-connect.types';

type LocationsTableContainerProps = {
  retailerNetworkUpdate: RetailerNetworkUpdateHandler;
  onClose: () => void;
  customerId: string;
  retailerNetworkUuid: string;
};

export const LocationsTableContainer = ({
  retailerNetworkUpdate,
  onClose,
  customerId,
  retailerNetworkUuid,
}: LocationsTableContainerProps) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { loading, error, data, paginationProps } = useGetSiteManagementLocations({
    filter: {
      search,
      customerIdEquals: customerId,
      retailerNetworkUuidNotIn: [retailerNetworkUuid],
    },
  });

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const onSubmit = async () => {
    const newLocationUuids = Array.from(bulkSelectedItems);
    await retailerNetworkUpdate({ newLocationUuids }, [
      'retailerNetworkLocationsWithPerformanceExcerpt',
      'retailerNetworkLocations',
      'retailerNetwork',
    ]);
    onClose();
  };

  const headerActions: React.ReactNode = useMemo(() => {
    return (
      <SearchForm
        onSubmit={({ search }) => {
          setSearch(search);
        }}
      />
    );
  }, [setSearch]);

  const bulkSelectedItemsCount = bulkSelectedItems.size;

  return (
    <ContainerColumnLeft>
      <TableContainer>
        <TableHeader
          disableTitleTypography
          actions={headerActions}
          title={
            <Typography variant='h6' whiteSpace='nowrap'>
              Choose Locations to Add to Retailer Network
            </Typography>
          }
          bulkSelected={bulkSelectedItemsCount}
          bulkActions={
            <Button color='primary' variant='contained' onClick={onSubmit}>
              {`Add selected ${pluralize(bulkSelectedItemsCount, 'Location')}`}
            </Button>
          }
        />
        <LocationsTable
          loading={loading}
          data={data}
          error={error}
          paginationProps={paginationProps}
          bulkSelectedItems={bulkSelectedItems}
          handleBulkSelectMultiple={handleBulkSelectMultiple}
          handleBulkDeselectAll={handleBulkDeselectAll}
          handleItemSelect={handleItemSelect}
        />
      </TableContainer>
    </ContainerColumnLeft>
  );
};
